'use client';

import { useRef, useEffect } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import RailBookCard from './RailBookCard';

interface AudioBook {
  id: string;
  title: string;
  prompt: string;
  coverImageUrl: string | null;
  pageCount: number;
  totalDuration: number | null;
  language: string;
  isFeatured: boolean;
  isFeaturedSample?: boolean;
  montessoriMode: boolean;
  ageBucket: string | null;
  hashtags: string;
  progress?: number;
  bilingualMode?: boolean;
  isLearningContent?: boolean;
  sourceLanguage?: string;
  targetLanguage?: string;
}

interface HorizontalRailProps {
  title: string;
  icon?: React.ReactNode;
  books: AudioBook[];
  rail?: string;
  onBookClick?: (book: AudioBook, rail: string, slotIndex: number) => void;
  onImpression?: (rail: string, books: AudioBook[]) => void;
  userFavorites?: Set<string>;
  userWatchlist?: Set<string>;
  onFavoriteToggle?: (bookId: string, isAdding: boolean) => Promise<void>;
  onWatchlistToggle?: (bookId: string, isAdding: boolean) => Promise<void>;
}

export default function HorizontalRail({
  title,
  icon,
  books,
  rail = 'unknown',
  onBookClick,
  onImpression,
  userFavorites,
  userWatchlist,
  onFavoriteToggle,
  onWatchlistToggle,
}: HorizontalRailProps) {
  const railRef = useRef<HTMLDivElement>(null);
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const impressionFiredRef = useRef(false);

  // Intersection Observer: fire impression when rail is 50% visible
  useEffect(() => {
    if (!railRef.current || !onImpression || impressionFiredRef.current) return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && !impressionFiredRef.current) {
          impressionFiredRef.current = true;
          onImpression(rail, books);
        }
      },
      { threshold: 0.5 }
    );

    observer.observe(railRef.current);
    return () => observer.disconnect();
  }, [rail, books, onImpression]);

  const scroll = (direction: 'left' | 'right') => {
    if (!scrollContainerRef.current) return;

    const scrollAmount = scrollContainerRef.current.clientWidth * 0.8;
    const newScrollLeft =
      direction === 'left'
        ? scrollContainerRef.current.scrollLeft - scrollAmount
        : scrollContainerRef.current.scrollLeft + scrollAmount;

    scrollContainerRef.current.scrollTo({
      left: newScrollLeft,
      behavior: 'smooth',
    });
  };

  if (books.length === 0) return null;

  return (
    <section
      ref={railRef}
      style={{
        marginBottom: '3rem',
        position: 'relative',
      }}
    >
      {/* Rail Header */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: '0.75rem',
          marginBottom: '1rem',
          paddingLeft: '2rem',
        }}
      >
        {icon && (
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: '36px',
              height: '36px',
              borderRadius: '10px',
              background: 'rgba(255, 255, 255, 0.15)',
              backdropFilter: 'blur(10px)',
              color: 'white',
            }}
          >
            {icon}
          </div>
        )}
        <h2
          style={{
            fontSize: 'clamp(1.25rem, 2.5vw, 1.75rem)',
            fontWeight: 800,
            margin: 0,
            background: 'linear-gradient(135deg, #ffffff 0%, rgba(255, 255, 255, 0.9) 100%)',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            backgroundClip: 'text',
          }}
        >
          {title}
        </h2>
        <span
          style={{
            padding: '0.25rem 0.75rem',
            background: 'rgba(255, 255, 255, 0.15)',
            backdropFilter: 'blur(10px)',
            borderRadius: '10px',
            fontSize: '0.875rem',
            fontWeight: 600,
            color: 'white',
          }}
        >
          {books.length}
        </span>
      </div>

      {/* Scroll Container with Cards */}
      <div style={{ position: 'relative', paddingLeft: '2rem', paddingRight: '2rem' }}>
        {/* Left Scroll Button */}
        <button
          onClick={() => scroll('left')}
          style={{
            position: 'absolute',
            left: '0.5rem',
            top: '50%',
            transform: 'translateY(-50%)',
            zIndex: 10,
            width: '48px',
            height: '48px',
            borderRadius: '50%',
            background: 'rgba(0, 0, 0, 0.7)',
            backdropFilter: 'blur(10px)',
            border: '2px solid rgba(255, 255, 255, 0.2)',
            color: 'white',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            transition: 'all 0.2s',
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.background = 'rgba(0, 0, 0, 0.9)';
            e.currentTarget.style.transform = 'translateY(-50%) scale(1.1)';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.background = 'rgba(0, 0, 0, 0.7)';
            e.currentTarget.style.transform = 'translateY(-50%) scale(1)';
          }}
          aria-label="Scroll left"
        >
          <ChevronLeft size={24} />
        </button>

        {/* Right Scroll Button */}
        <button
          onClick={() => scroll('right')}
          style={{
            position: 'absolute',
            right: '0.5rem',
            top: '50%',
            transform: 'translateY(-50%)',
            zIndex: 10,
            width: '48px',
            height: '48px',
            borderRadius: '50%',
            background: 'rgba(0, 0, 0, 0.7)',
            backdropFilter: 'blur(10px)',
            border: '2px solid rgba(255, 255, 255, 0.2)',
            color: 'white',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            transition: 'all 0.2s',
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.background = 'rgba(0, 0, 0, 0.9)';
            e.currentTarget.style.transform = 'translateY(-50%) scale(1.1)';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.background = 'rgba(0, 0, 0, 0.7)';
            e.currentTarget.style.transform = 'translateY(-50%) scale(1)';
          }}
          aria-label="Scroll right"
        >
          <ChevronRight size={24} />
        </button>

        {/* Horizontal Scroll Container */}
        <div
          ref={scrollContainerRef}
          style={{
            display: 'flex',
            gap: '1rem',
            overflowX: 'auto',
            overflowY: 'hidden',
            scrollbarWidth: 'none',
            msOverflowStyle: 'none',
            paddingBottom: '0.5rem',
          }}
          className="hide-scrollbar"
        >
          {books.map((book, index) => (
            <div
              key={book.id}
              onClick={() => onBookClick?.(book, rail, index)}
              style={{
                flexShrink: 0,
                cursor: 'pointer',
              }}
            >
              <RailBookCard
                book={book}
                animationDelay={index * 50}
                isFavorited={userFavorites?.has(book.id)}
                isInWatchlist={userWatchlist?.has(book.id)}
                onFavoriteToggle={onFavoriteToggle}
                onWatchlistToggle={onWatchlistToggle}
              />
            </div>
          ))}
        </div>
      </div>

      <style jsx>{`
        .hide-scrollbar::-webkit-scrollbar {
          display: none;
        }
      `}</style>
    </section>
  );
}

